import React, { useState, useEffect, useCallback } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { buildTopCategoriesModel, buildHourlyPerformanceModel } from '../../../lib/chartModels';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Progress } from '../../ui/progress';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  BarChart3,
  Brain,
  TrendingUp,
  Target,
  Clock,
  Users,
  CheckCircle,
  AlertTriangle,
  Zap,
  Star,
  RefreshCw,
  Download,
  Calendar,
  Activity
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const AIAnalytics = () => {
  const { currentBusiness } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState({});
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // D4 — canonical Firebase-authenticated request (fresh ID token + backend base + 401 refresh).
  const { makeAuthenticatedRequest } = useCRMAuth();

  // Fetch AI analytics data (component-scope so the Refresh button can re-invoke the real fetch)
  const fetchAnalytics = useCallback(async () => {
    if (!currentBusiness?.brandId) return;

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/ai-agent/analytics/${currentBusiness.brandId}?period=${timeRange}`
      );

      if (response.ok) {
        const data = await response.json();
        // Normalize the backend shape (metrics.* snake_case + category_analysis + analytics_data) into the
        // camelCase fields this component renders. Fields the endpoint does not provide stay null -> '—'
        // (never fabricated). This is why every KPI/sub-tab previously showed '—'/0/empty.
        const m = data.metrics || {};
        const catCounts = (data.category_analysis && data.category_analysis.counts) || {};
        const total = m.total_conversations || 0;
        const topCategories = buildTopCategoriesModel(catCounts, total);
        const daily = (data.analytics_data && data.analytics_data.daily_volume) || {};
        const hourlyPerformance = buildHourlyPerformanceModel(daily);
        setMetrics({
          totalConversations: m.total_conversations != null ? m.total_conversations : null,
          autoResolutionRate: m.auto_resolution_rate != null ? m.auto_resolution_rate : null,
          escalationRate: m.escalation_rate != null ? m.escalation_rate : null,
          successfulResolutions: m.successful_resolutions != null ? m.successful_resolutions : null,
          confidenceScore: m.average_confidence != null ? m.average_confidence : null,
          // Not provided by this endpoint — honest '—', never a fake 0.
          avgResponseTime: null,
          customerSatisfaction: null,
          topCategories,
          hourlyPerformance,
        });
      } else {
        // D4 — no mock fallback: surface a truthful failure instead of fabricating analytics.
        throw new Error(`Failed to load AI analytics (HTTP ${response.status})`);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching AI analytics:', error);
      toast({
        title: t('common.error'),
        description: t('ak.failedLoadAnalytics'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.brandId, timeRange, makeAuthenticatedRequest, toast]);

  useEffect(() => {
    fetchAnalytics();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchAnalytics, 300000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  // Real refresh: re-invoke the actual analytics fetch (no fake "refreshed" toast).
  const handleRefresh = () => {
    fetchAnalytics();
  };

  const handleExport = () => {
    const data = {
      businessId: currentBusiness?.brandId,
      timeRange,
      generatedAt: new Date().toISOString(),
      metrics
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analytics-${currentBusiness?.brandId}-${timeRange}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: t('ak.exportComplete'),
      description: t('ak.analyticsExportedDesc'),
    });
  };

  if (loading && !metrics.totalConversations) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('ak.loadingAnalytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('ak.analyticsDashboardTitle')}</h1>
              <p className="text-gray-600">{t('ak.analyticsDashboardSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">{t('ak.range.24h')}</SelectItem>
                <SelectItem value="7d">{t('ak.range.7d')}</SelectItem>
                <SelectItem value="30d">{t('ak.range.30d')}</SelectItem>
                <SelectItem value="90d">{t('ak.range.90d')}</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>

            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              {t('ak.export')}
            </Button>
          </div>
        </div>

        {/* Last Updated */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <Clock className="h-4 w-4" />
          <span>{t('ak.lastUpdated', { time: formatInTenantTz(lastUpdated, { timeStyle: 'medium' }) })}</span>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.totalConversations')}</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.totalConversations != null ? metrics.totalConversations.toLocaleString() : '—'}</p>
                  <p className="text-xs text-gray-500">{t('ak.aiHandled')}</p>
                </div>
                <Brain className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.autoResolutionRate')}</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.autoResolutionRate != null ? `${metrics.autoResolutionRate.toFixed(1)}%` : '—'}</p>
                  {/* Trend deltas not provided by backend yet. real wiring lands in the AI backend phase */}
                  <p className="text-xs text-gray-500">{t('ak.currentPeriod')}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.avgResponseTime')}</p>
                  <p className="text-2xl font-bold text-purple-600">{metrics.avgResponseTime != null ? `${metrics.avgResponseTime}s` : '—'}</p>
                  {/* Trend deltas not provided by backend yet. real wiring lands in the AI backend phase */}
                  <p className="text-xs text-gray-500">{t('ak.currentPeriod')}</p>
                </div>
                <Zap className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.customerSatisfaction')}</p>
                  <p className="text-2xl font-bold text-orange-600">{metrics.customerSatisfaction != null ? `${metrics.customerSatisfaction}/5.0` : '—'}</p>
                  {/* Trend deltas not provided by backend yet. real wiring lands in the AI backend phase */}
                  <p className="text-xs text-gray-500">{t('ak.currentPeriod')}</p>
                </div>
                <Star className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">{t('ak.performanceOverview')}</TabsTrigger>
          <TabsTrigger value="categories">{t('ak.tab.categoryAnalysis')}</TabsTrigger>
          <TabsTrigger value="trends">{t('ak.tab.trendsPatterns')}</TabsTrigger>
          <TabsTrigger value="insights">{t('ak.tab.aiInsights')}</TabsTrigger>
        </TabsList>

        {/* Performance Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('ak.resolutionPerformance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Honest '—' when a field is null (not provided) or undefined (fetch failed) —
                      never an authoritative 0/0%. Progress bars stay at 0 width for missing values. */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('ak.successfulResolutions')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-green-600">{metrics.successfulResolutions != null ? metrics.successfulResolutions : '—'}</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                  </div>
                  <Progress value={metrics.autoResolutionRate ?? 0} className="h-3" />

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('ak.escalationRate')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-orange-600">{metrics.escalationRate != null ? `${metrics.escalationRate.toFixed(1)}%` : '—'}</span>
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                    </div>
                  </div>
                  <Progress value={metrics.escalationRate ?? 0} className="h-3" />

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('ak.aiConfidenceScore')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-purple-600">{metrics.confidenceScore != null ? `${metrics.confidenceScore.toFixed(1)}%` : '—'}</span>
                      <Target className="h-4 w-4 text-purple-500" />
                    </div>
                  </div>
                  <Progress value={metrics.confidenceScore ?? 0} className="h-3" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('ak.hourlyPerformance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {(metrics.hourlyPerformance || []).map((hour, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <Badge variant="outline" className="text-xs">{hour.hour}</Badge>
                        <span className="text-sm">{t('ak.conversationsCount', { count: hour.conversations })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Progress value={hour.success} className="w-16 h-2" />
                        <span className="text-xs font-semibold text-green-600">{hour.success}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Category Analysis Tab */}
        <TabsContent value="categories" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('ak.performanceByCategory')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(metrics.topCategories || []).map((category, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-white/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <Badge className="bg-blue-100 text-blue-800">
                          {category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                        </Badge>
                        <span className="text-sm text-gray-600">{t('ak.conversationsCount', { count: category.count })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        <span className="font-semibold text-green-600">{category.success_rate}%</span>
                      </div>
                    </div>
                    <Progress value={category.success_rate} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends & Patterns Tab */}
        <TabsContent value="trends" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('ak.performanceTrends')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Wired to real daily conversation volume (analytics_data.daily_volume). Honest empty
                    state only when the tenant has no volume in the selected range. */}
                {(metrics.hourlyPerformance && metrics.hourlyPerformance.length) ? (
                  <div className="space-y-2">
                    {(() => { const max = Math.max(...metrics.hourlyPerformance.map(d => d.count || 0), 1);
                      return metrics.hourlyPerformance.map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-24 shrink-0">{d.hour}</span>
                          <div className="flex-1 bg-gray-100 rounded h-3">
                            <div className="bg-blue-500 h-3 rounded" style={{ width: `${Math.round((d.count / max) * 100)}%` }} />
                          </div>
                          <span className="text-xs text-gray-700 w-8 text-right">{d.count}</span>
                        </div>
                      )); })()}
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-40" />
                    <p>{t('ak.trendComparisonsUnavailable')}</p>
                    <p className="text-sm text-gray-400">{t('ak.trendComparisonsDesc')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('ak.usagePatterns')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Usage-pattern analytics (peak hours, common issues, resolution distribution) are not
                    derived by any backend endpoint yet — honest placeholders, not fabricated figures.
                    real wiring lands in the AI backend phase */}
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-sm font-medium text-gray-900 mb-1">{t('ak.peakHours')}</div>
                    <div className="text-xs text-gray-600">{
                      (metrics.hourlyPerformance && metrics.hourlyPerformance.length)
                        ? (() => { const top = [...metrics.hourlyPerformance].sort((a, b) => b.count - a.count)[0];
                            return `${top.hour} (${top.count})`; })()
                        : t('ak.notAvailableYet')
                    }</div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-sm font-medium text-gray-900 mb-1">{t('ak.mostCommonIssues')}</div>
                    <div className="text-xs text-gray-600">{
                      (metrics.topCategories && metrics.topCategories.length)
                        ? metrics.topCategories.slice(0, 3).map(c => `${c.category} (${c.count})`).join(', ')
                        : t('ak.notAvailableYet')
                    }</div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-sm font-medium text-gray-900 mb-1">{t('ak.aiModelPerformance')}</div>
                    <div className="text-xs text-gray-600">{t('ak.autoResolutionRate')}: {metrics.autoResolutionRate != null ? `${metrics.autoResolutionRate.toFixed(1)}%` : '—'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-purple-600" />
                  <span>{t('ak.aiPerformanceInsights')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* These insight breakdowns were fabricated (per-category success/escalation rates,
                    model learning %) — no endpoint derives them yet. Honest empty state instead.
                    real wiring lands in the AI backend phase */}
                <div className="text-center py-10 text-gray-500">
                  <Brain className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>{t('ak.aiInsightsUnavailable')}</p>
                  <p className="text-sm text-gray-400">{t('ak.aiInsightsDesc')}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('ak.bestPracticeGuidance')}</CardTitle>
                {/* Honest labelling: these are general AI best-practices, NOT generated from this
                    tenant's live data. No per-business recommendations endpoint exists, so we do not
                    present these as data-derived insights. */}
                <p className="text-xs text-gray-500 mt-1">
                  {t('ak.bestPracticeNote')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2 flex items-center">
                      <Activity className="h-4 w-4 mr-2" />
                      {t('ak.immediateActions')}
                    </h4>
                    <ul className="text-sm text-purple-700 space-y-2">
                      <li>{t('ak.immediate1')}</li>
                      <li>{t('ak.immediate2')}</li>
                      <li>{t('ak.immediate3')}</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h4 className="font-medium text-indigo-800 mb-2 flex items-center">
                      <Target className="h-4 w-4 mr-2" />
                      {t('ak.strategicImprovements')}
                    </h4>
                    <ul className="text-sm text-indigo-700 space-y-2">
                      <li>{t('ak.strategic1')}</li>
                      <li>{t('ak.strategic2')}</li>
                      <li>{t('ak.strategic3')}</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-medium text-teal-800 mb-2 flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      {t('ak.teamDevelopment')}
                    </h4>
                    <ul className="text-sm text-teal-700 space-y-2">
                      <li>{t('ak.team1')}</li>
                      <li>{t('ak.team2')}</li>
                      <li>{t('ak.team3')}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAnalytics;